import { Box, CssBaseline } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import BrandedSpinner from './components/common/BrandedSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import SmartRagMigrationManager from './components/common/SmartRagMigrationManager';
import TermsUpdateBanner from './components/common/TermsUpdateBanner';
import DashboardLayout from './components/Layout/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAccount } from './contexts/AccountContext';
import { useAuth } from './contexts/AuthContext';
import { BillingProvider } from './contexts/BillingContext';
import { InsightsDashboardProvider } from './contexts/InsightsDashboardContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PricingProvider } from './contexts/PricingContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext'; // Import WorkspaceProvider
import AccountPage from './pages/AccountPage'; // Import Account page
import AddTenderPage from './pages/AddTenderPage'; // Import Add Tender page
import AdminPage from './pages/AdminPage'; // Import Admin page
import ArchivePage from './pages/Archive/ArchivePage';
import AssignmentsDashboard from './pages/Assignments/AssignmentsDashboard';
import AuthBridgePage from './pages/AuthBridgePage'; // Import AuthBridgePage
import BillingPage from './pages/BillingPage';
import ClientDetailsPage from './pages/ClientDetailsPage'; // Import Client Details page
import CompanyInfoPage from './pages/CompanyInfoPage'; // Import User Details Settings page
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage'; // Import Forgot Password page
import HelpPage from './pages/HelpPage'; // Import Help page
import InsightsDashboard from './pages/InsightsDashboard'; // Import the new InsightsDashboard
import LoginPage from './pages/LoginPage';
import ManualTenderPage from './pages/ManualTenderPage';
import OnboardingWizard from './pages/OnboardingWizard/OnboardingWizard'; // Import Onboarding Wizard
import PartnerDetailsPage from './pages/PartnerDetailsPage';
import PartnerInvitationPage from './pages/PartnerInvitationPage';
import PartnerRegistrationPage from './pages/PartnerRegistrationPage';
import PartnersPage from './pages/PartnersPage'; // Import PartnersPage
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ProcurementDetailPage from './pages/ProcurementDetailPage'; // Import ProcurementDetailPage
import ProcurementsDashboard from './pages/ProcurementsDashboard'; // Import ProcurementsDashboard
import PublicTendersPage from './pages/PublicTendersPage'; // Import Public Tenders page
import SharepointGuidePage from './pages/SharepointGuidePage'; // Import the new guide page
import SignupPage from './pages/SignupPage'; // IMPORT THE NEW SIGNUP PAGE
import SmsVerificationPage from './pages/SmsVerificationPage'; // Import SMS Verification page
import TeamDetailsPage from './pages/TeamDetailsPage'; // Import Team Details page
import TemplatePage from './pages/TemplatePage'; // Import TemplatePage
import TenderDetailsPage from './pages/TenderDetailsPage';
import TermsPage from './pages/TermsPage';
import UserDetailsPage from './pages/UserDetailsPage'; // Import User Details page
import UserGuidePage from './pages/UserGuidePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WelcomePage from './pages/WelcomePage'; // Import Welcome page
import type { Account } from './types/account';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const AUTH_PAGES = ['/login', '/signup', '/forgot-password'];
const PUBLIC_PAGES = [
  '/terms-conditions',
  '/privacy-policy',
  '/sharepoint-guide',
  '/verify-email',
  '/sms-verification',
  '/auth-bridge',
];

const ProtectedApp = () => {
  const { account } = useAccount();
  return (
    <PricingProvider>
      <BillingProvider>
        <NotificationProvider user={account}>
          <TermsUpdateBanner />
          <SmartRagMigrationManager />
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </NotificationProvider>
      </BillingProvider>
    </PricingProvider>
  );
};

// Check authenticated user redirects and return redirect path or null
const getAuthenticatedRedirect = (
  account: Account,
  pathname: string,
  isAuthPage: boolean,

  isLoggingIn: boolean
): string | null => {
  if (!account.emailVerified && pathname !== '/verify-email') {
    return '/verify-email';
  }
  // SMS verification is temporarily disabled
  // if (account.emailVerified && !account.phoneNumber && pathname !== '/sms-verification') {
  //   return '/sms-verification';
  // }
  // if (account.emailVerified && account.phoneNumber && pathname === '/sms-verification') {
  //   return '/dashboard';
  // }

  // Force users through onboarding if they haven't completed it
  const isOnboardingComplete =
    account.preferences?.onboardingCompleted || account.company_registration;

  const hasBiddingModule = account.clientSettings?.enableBiddingModule !== false;

  if (!isOnboardingComplete && pathname !== '/onboarding') {
    return '/onboarding';
  }

  if (isAuthPage && !isLoggingIn) {
    if (!isOnboardingComplete) {
      return '/onboarding';
    }
    return hasBiddingModule ? '/dashboard' : '/procure/dashboard';
  }
  return null;
};

// This component determines layout based on route and authentication state
export const AppContent = () => {
  const { isAuthenticated, isLoading: isAuthLoading, isLoggingOut, isLoggingIn } = useAuth();
  const { account, isAccountLoading } = useAccount();
  const location = useLocation();

  const isAuthPage = AUTH_PAGES.includes(location.pathname);
  const isPublicPage = PUBLIC_PAGES.includes(location.pathname);

  if (isLoggingOut) {
    return <BrandedSpinner message="Signing out..." />;
  }

  // We must wait for BOTH auth to finish initializing AND account details to finish fetching
  const isLoading = isAuthLoading || (isAuthenticated && isAccountLoading) || isLoggingIn;

  if (isLoading) {
    return <BrandedSpinner message="Loading, please wait..." />;
  }

  if (isAuthenticated && account) {
    const redirect = getAuthenticatedRedirect(account, location.pathname, isAuthPage, isLoggingIn);
    if (redirect) {
      return <Navigate to={redirect} replace />;
    }
  } else if (!isAuthenticated && !isPublicPage && !isAuthPage) {
    return <Navigate to="/login" replace />;
  }

  // If none of the above conditions are met, render the routes as defined.
  return (
    <WorkspaceProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <ScrollRestoration />
        <Outlet />
      </Box>
    </WorkspaceProvider>
  );
};

const CatchAllRoute = () => {
  const { isAuthenticated } = useAuth();
  const { account } = useAccount();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const hasBiddingModule = account?.clientSettings?.enableBiddingModule !== false;
  return <Navigate to={hasBiddingModule ? '/dashboard' : '/procure/dashboard'} replace />;
};

const router = createBrowserRouter([
  {
    element: <AppContent />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/sharepoint-guide', element: <SharepointGuidePage /> },
      // Public routes
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/sms-verification', element: <SmsVerificationPage /> },
      { path: '/terms-conditions', element: <TermsPage /> },
      { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
      { path: '/auth-bridge', element: <AuthBridgePage /> },

      // Protected routes
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/welcome', element: <WelcomePage /> },
          { path: '/onboarding', element: <OnboardingWizard /> },
          {
            element: <ProtectedApp />,
            children: [
              { path: '/tenders/new', element: <AddTenderPage /> },
              { path: '/dashboard', element: <DashboardPage /> },
              { path: '/procure/dashboard', element: <ProcurementsDashboard /> },
              { path: '/procure/detail/:procurementId', element: <ProcurementDetailPage /> },
              { path: '/archive', element: <ArchivePage /> },
              { path: '/assignments', element: <AssignmentsDashboard /> },
              { path: '/public-tenders', element: <PublicTendersPage /> },
              { path: '/partners', element: <PartnersPage /> },
              {
                path: '/insights',
                element: (
                  <InsightsDashboardProvider>
                    <InsightsDashboard />
                  </InsightsDashboardProvider>
                ),
              },
              { path: '/templates', element: <TemplatePage /> },
              { path: '/company-info', element: <CompanyInfoPage /> },
              { path: '/account', element: <AccountPage /> },
              { path: '/billing', element: <BillingPage /> },
              { path: '/partner-registration', element: <PartnerRegistrationPage /> },
              { path: '/partner-details/:partnerId', element: <PartnerDetailsPage /> },
              { path: '/partner-invitation/:tenderId', element: <PartnerInvitationPage /> },
              { path: '/tender/:tenderId', element: <TenderDetailsPage /> },
              { path: '/manual-tender', element: <ManualTenderPage /> },
              { path: '/admin', element: <AdminPage /> },
              { path: '/admin/:tab', element: <AdminPage /> },
              { path: '/admin/client/:clientId', element: <ClientDetailsPage /> },
              { path: '/admin/team/:teamId', element: <TeamDetailsPage /> },
              { path: '/admin/user/:userId', element: <UserDetailsPage /> },
              { path: '/user-guide', element: <UserGuidePage /> },
              { path: '/help', element: <HelpPage /> },
              { index: true, element: <CatchAllRoute /> },
            ],
          },
        ],
      },
      { path: '*', element: <CatchAllRoute /> },
    ],
  },
]);

function App() {
  return (
    <ErrorBoundary>
      <SnackbarProvider>
        <ThemeProvider>
          <CssBaseline />
          {/* Router needs to be the parent of any component using router hooks like useLocation */}
          <Elements stripe={stripePromise}>
            <RouterProvider router={router} />
          </Elements>
        </ThemeProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}

export default App;
